import React, { useState } from 'react';

const FileUpload = ({ onFileUpload }) => {
    const [dragActive, setDragActive] = useState(false);
    const [isUploading, setIsUploading] = useState(false);

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        const files = e.dataTransfer.files;
        if (files && files[0]) {
            handleFile(files[0]);
        }
    };

    const handleChange = (e) => {
        e.preventDefault();
        if (e.target.files && e.target.files[0]) {
            handleFile(e.target.files[0]);
        }
    };

    const handleFile = async (file) => {
        if (file.type !== 'application/json') {
            alert('Please upload a JSON file');
            return;
        }

        setIsUploading(true);

        try {
            const text = await file.text();
            const data = JSON.parse(text);

            // Validate data structure - now includes tripCost
            if (Array.isArray(data) && data.every(item =>
                item.destination &&
                item.travelType &&
                item.status !== undefined &&
                item.isFavorite !== undefined &&
                item.tripCost !== undefined &&
                !isNaN(parseInt(item.tripCost))
            )) {
                onFileUpload(data);
                alert(`Successfully uploaded ${data.length} travel destinations!`);
            } else {
                alert('Invalid file format. Please upload a valid travel destinations JSON file with destination, travelType, status, isFavorite, and tripCost fields.');
            }
        } catch (error) {
            alert('Error reading file. Please make sure it\'s a valid JSON file.');
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <>
            {/* Separator line */}
            <div className="separator-container my-8">
                <div className="flex items-center">
                    <div className="flex-grow h-px bg-gradient-to-r from-transparent via-green-300 to-transparent"></div>
                    <div className="px-4 text-sm text-gray-500 font-medium bg-gray-100 rounded-full"></div>
                    <div className="flex-grow h-px bg-gradient-to-r from-transparent via-green-300 to-transparent"></div>
                </div>
            </div>

            <div className="file-upload-section mb-8" id="file-upload-section" data-test-id="file-upload-section">
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg shadow-lg p-6 border-l-4 border-green-500">
                    <h3 className="text-2xl font-bold mb-4 text-center text-green-800">Upload Your Next Planned Trip</h3>
                    <p className="text-gray-700 text-center mb-6 text-sm font-medium">
                        Upload a JSON file with your travel destinations to populate the table
                    </p>

                    <div
                        className={`border-2 border-dashed rounded-lg p-8 text-center transition-all duration-300 ${dragActive
                            ? 'border-green-500 bg-green-100 transform scale-105'
                            : 'border-green-300 hover:border-green-400 bg-white'
                            } ${isUploading ? 'opacity-50 pointer-events-none' : ''}`}
                        onDragEnter={handleDrag}
                        onDragLeave={handleDrag}
                        onDragOver={handleDrag}
                        onDrop={handleDrop}
                    >
                        <div className="mb-4">
                            <svg className="mx-auto h-16 w-16 text-green-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                                <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </div>

                        <div className="mb-4">
                            <p className="text-lg font-semibold text-gray-900 mb-2">
                                {isUploading ? 'Processing file...' : 'Drop your JSON file here'}
                            </p>
                            <p className="text-sm text-gray-600">
                                or click to browse files
                            </p>
                        </div>

                        <input
                            type="file"
                            id="file-upload"
                            className="hidden"
                            accept=".json"
                            onChange={handleChange}
                            disabled={isUploading}
                            data-test-id="file-upload-input"
                        />

                        <label
                            htmlFor="file-upload"
                            className={`inline-flex items-center px-6 py-3 border-2 border-green-500 text-base font-semibold rounded-lg text-green-700 bg-green-50 hover:bg-green-100 cursor-pointer transition-all duration-200 ${isUploading ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-md transform hover:scale-105'
                                }`}
                        >
                            {isUploading ? 'Uploading...' : 'Choose File'}
                        </label>
                    </div>

                    <div className="mt-4 text-center">
                        <p className="text-xs text-gray-600 font-medium">
                            Accepted: JSON files (.json)
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                            Format: Array with destination, travelType, status, and isFavorite fields
                        </p>
                    </div>
                </div>
            </div>
        </>
    );
};

export default FileUpload;